import React, {useState} from "react";
import {useCollapsibleRows} from "@vigo/hooks";
import {Card, Button, Tooltip, Row, message, Popconfirm, Space} from "antd";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {Link} from "react-router-dom";
import {BackofficeConvertedToCashTabFilters} from "./BackofficeConvertedToCashTabFilters";
import moment from "moment";
import {
    codPaymentStatuses,
    codPaymentApproveStatuses,
    codPaymentTypes
} from "../../../../constants/constants";
import {isConstantExist} from "../../../../utils/helpers";
import {useTranslation} from "react-i18next";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {ExpandableStyledTable} from "components/_defaultComponents/ExpandableStyledTable/ExpandableStyledTable.styles";

const PAGE_SIZE = 10;
const formatDate = date => {
    return moment(date).format("DD.MM.YYYY HH:mm");
};
export const BackofficeConvertedToCashTab = ({isTabOpened = false}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const expandableState = useCollapsibleRows({isTabOpened, page});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [recall, setRecall] = useState(false);

    const [loading, payments] = useFetch({
        fetchFn: stores.codPaymentStore.getConvertedToCashPayments.bind(null, {
            ...filters,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [filters, page, isTabOpened, recall],
        initialCall: isTabOpened,
        condition: !stores.meStore.checkRole("isCompanyUser") && isTabOpened
    });

    const expandedRowRender = record => {
        const columns = [
            {
                title: "Transaction Id",
                dataIndex: "transactionId",
                key: "transactionId",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: text => <Tooltip title={text}>{text}</Tooltip>,
                onCell: () => ({
                    "data-label": "Transaction Id"
                })
            },
            {
                title: "Ödeme Tutarı",
                dataIndex: "transactionAmount",
                key: "amount",
                width: 100,
                render: text => text && `${text} TL`,
                onCell: () => ({
                    "data-label": "Ödeme Tutarı"
                })
            },
            {
                title: "Ödeme yöntemi",
                dataIndex: "paymentType",
                key: "paymentMethod",
                width: 120,
                render: paymentType =>
                    `${
                        isConstantExist(codPaymentTypes, paymentType)
                            ? t(`paymentTypes.${paymentType}`, {
                                  ns: "codPayment"
                              })
                            : paymentType
                    }`,
                onCell: () => ({
                    "data-label": "Ödeme Yöntemi"
                })
            },
            {
                title: "İşlem Tarihi",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 150,
                render: text => new Date(text).toLocaleString("tr-TR"),
                onCell: () => ({
                    "data-label": "İşlem Tarihi"
                })
            }
        ];

        return (
            <ResponsiveDataTable
                columns={columns}
                dataSource={record.transactions || []}
                pagination={false}
                rowKey="_id"
                size="small"
            />
        );
    };

    const columns = [
        {
            title: "Firma",
            dataIndex: ["company", "companyName"],
            key: "company",
            render: (text, row) => (
                <Link
                    data-testid="companyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/" + row?.company?._id
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Firma"
            })
        },
        {
            title: "Alt Firma",
            dataIndex: ["subCompany", "name"],
            key: "subCompany",
            render: (text, row) => (
                <Link
                    data-testid="subCompanyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/sub-company/" + row?.subCompany?._id
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Alt Firma"
            })
        },
        {
            title: "Ödeme Id",
            dataIndex: "_id",
            key: "_id",
            render: text => text,
            onCell: () => ({
                "data-label": "Ödeme Id"
            })
        },
        {
            title: "Tutar",
            dataIndex: ["payment", "receivedAmount"],
            key: "amount",
            render: text => text && `${text} TL`,
            onCell: () => ({
                "data-label": "Tutar"
            })
        },
        {
            title: "İşlem Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: text => text && formatDate(text),
            onCell: () => ({
                "data-label": "İşlem Tarihi"
            })
        },
        {
            title: "Ödeme Yöntemi",
            dataIndex: ["payment", "paymentType"],
            key: "paymentMethod",
            render: paymentType =>
                `${
                    isConstantExist(codPaymentTypes, paymentType)
                        ? t(`paymentTypes.${paymentType}`, {
                              ns: "codPayment"
                          })
                        : paymentType
                }`,
            onCell: () => ({
                "data-label": "Ödeme Yöntemi"
            })
        },
        {
            title: "İşlem Durumu",
            dataIndex: ["status"],
            key: "status",
            render: text => {
                const status = codPaymentStatuses?.find(status => status?.value === text);
                return status?.name || text;
            },
            onCell: () => ({
                "data-label": "İşlem Durumu"
            })
        },
        {
            title: "Onay Durumu",
            dataIndex: ["payment", "approveStatus"],
            key: "approveStatus",
            render: (approveStatus, row) => {
                const status = codPaymentApproveStatuses?.find(
                    status => status?.value === approveStatus
                );
                return <Tooltip title={status?.name}>{status?.name}</Tooltip>;
            },
            onCell: () => ({
                "data-label": "Onay Durumu"
            })
        },
        {
            title: "Sipariş No",
            dataIndex: ["delivery", "vigoOrderNumber"],
            key: "vigoOrderNumber",
            render: (text, row) => (
                <Link
                    data-testid="vigoOrderNumber"
                    target="_blank"
                    to={{
                        pathname: "/orders/" + row?.deliveryInfo?._id
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Sipariş No"
            })
        },
        {
            title: "Kurye",
            dataIndex: ["deliveryInfo", "assignedCourier", "fullname"],
            key: "courier",
            render: (text, row) => (
                <Link
                    data-testid="courierName"
                    target="_blank"
                    to={{
                        pathname: "/couriers/" + row?.deliveryInfo?.assignedCourier?._id
                    }}
                >
                    <>{text}</>
                </Link>
            ),
            onCell: () => ({
                "data-label": "Kurye"
            })
        },
        {
            title: "Aksiyonlar",
            key: "actions",
            width: 150,
            render: (_, record) => {
                if (record?.payment?.approveStatus) {
                    return <span>İşlem yapıldı</span>;
                }

                return (
                    <Space size="small">
                        <Popconfirm
                            title="Ödemeyi onaylamak istediğinize emin misiniz?"
                            onConfirm={() => handleApprovePayment(record, "APPROVED")}
                            okText="Evet"
                            cancelText="Hayır"
                        >
                            <Button size="small" type="primary">
                                Onayla
                            </Button>
                        </Popconfirm>
                        <Popconfirm
                            title="Ödemeyi fraud olarak işaretlemek istediğinize emin misiniz?"
                            onConfirm={() => handleApprovePayment(record, "FRAUD")}
                            okText="Evet"
                            cancelText="Hayır"
                        >
                            <Button size="small" danger>
                                Fraud
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
            onCell: () => ({
                "data-label": "Aksiyonlar"
            })
        }
    ];

    const handleApprovePayment = async (record, status) => {
        try {
            const result = await stores.codPaymentStore.approvePayment({
                codPaymentId: record?._id,
                status
            });

            if (!result) {
                throw new Error("İşlem yapılırken bir hata oluştu");
            } else if (result?.success) {
                message.success(result?.message);
                setRecall(!recall);
            } else {
                message.error(result?.message);
            }
        } catch (error) {
            message.error("İşlem yapılırken bir hata oluştu");
        }
    };

    return (
        <>
            <Card>
                <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                    <BackofficeConvertedToCashTabFilters
                        filters={filters}
                        setFilters={setFilters}
                    />
                </Row>
                <ExpandableStyledTable
                    loading={loading}
                    columns={columns}
                    size="small"
                    dataSource={payments?.rows || []}
                    rowKey="_id"
                    expandable={{
                        ...expandableState,
                        expandedRowRender
                    }}
                    pagination={{
                        total: payments?.dataCount || 0,
                        current: page,
                        hideOnSinglePage: true,
                        showSizeChanger: false,
                        pageSize: PAGE_SIZE,
                        onChange: page => {
                            setPage(page);
                            setSelectedRowKeys([]);
                        }
                    }}
                />
            </Card>
        </>
    );
};
